import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Error handler
const handleError = (error) => {
  if (process.env.NODE_ENV === 'development') console.error('API Error:', error);
  if (error.response) {
    // Server responded with error
    throw new Error(error.response.data.detail || 'Server error occurred');
  } else if (error.request) {
    // Request made but no response
    throw new Error('No response from server. Please check your connection.');
  } else {
    // Something else happened
    throw new Error('An unexpected error occurred');
  }
};

// ========== PUBLIC API CALLS ==========

export const getHomeData = async () => {
  try {
    const response = await apiClient.get('/home');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getSettings = async () => {
  try {
    const response = await apiClient.get('/settings');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// Alias for clarity
export const getSiteSettings = getSettings;

// AI Chat APIs
export const sendChatMessage = async (data) => {
  try {
    const response = await apiClient.post('/ai/chat', data);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getChatHistory = async (sessionId) => {
  try {
    const response = await apiClient.get(`/ai/chat/history/${sessionId}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getRecommendations = async (page, interests = null) => {
  try {
    const params = { page };
    if (interests) params.interests = interests;
    const response = await apiClient.get('/ai/recommendations', { params });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getAbout = async () => {
  try {
    const response = await apiClient.get('/about');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getTherapies = async () => {
  try {
    const response = await apiClient.get('/therapies');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getTrainings = async () => {
  try {
    const response = await apiClient.get('/trainings');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getEvents = async () => {
  try {
    const response = await apiClient.get('/events');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getTestimonials = async () => {
  try {
    const response = await apiClient.get('/testimonials');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getReviews = async () => {
  try {
    const response = await apiClient.get('/reviews');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const getVlogs = async () => {
  try {
    const response = await apiClient.get('/vlogs');
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export const submitContactForm = async (formData) => {
  try {
    const response = await apiClient.post('/contact', formData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// ========== ADMIN API CALLS (for future admin panel) ==========

export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/admin/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// Admin CRUD operations
export const adminAPI = {
  // Therapies
  getTherapies: () => apiClient.get('/admin/therapies').then(res => res.data),
  createTherapy: (data) => apiClient.post('/admin/therapies', data).then(res => res.data),
  updateTherapy: (id, data) => apiClient.put(`/admin/therapies/${id}`, data).then(res => res.data),
  deleteTherapy: (id) => apiClient.delete(`/admin/therapies/${id}`).then(res => res.data),
  
  // Trainings
  getTrainings: () => apiClient.get('/admin/trainings').then(res => res.data),
  createTraining: (data) => apiClient.post('/admin/trainings', data).then(res => res.data),
  updateTraining: (id, data) => apiClient.put(`/admin/trainings/${id}`, data).then(res => res.data),
  deleteTraining: (id) => apiClient.delete(`/admin/trainings/${id}`).then(res => res.data),
  
  // Events
  getEvents: () => apiClient.get('/admin/events').then(res => res.data),
  createEvent: (data) => apiClient.post('/admin/events', data).then(res => res.data),
  updateEvent: (id, data) => apiClient.put(`/admin/events/${id}`, data).then(res => res.data),
  deleteEvent: (id) => apiClient.delete(`/admin/events/${id}`).then(res => res.data),
  
  // Testimonials
  getTestimonials: () => apiClient.get('/admin/testimonials').then(res => res.data),
  createTestimonial: (data) => apiClient.post('/admin/testimonials', data).then(res => res.data),
  updateTestimonial: (id, data) => apiClient.put(`/admin/testimonials/${id}`, data).then(res => res.data),
  deleteTestimonial: (id) => apiClient.delete(`/admin/testimonials/${id}`).then(res => res.data),
  
  // Reviews
  getReviews: () => apiClient.get('/admin/reviews').then(res => res.data),
  createReview: (data) => apiClient.post('/admin/reviews', data).then(res => res.data),
  updateReview: (id, data) => apiClient.put(`/admin/reviews/${id}`, data).then(res => res.data),
  deleteReview: (id) => apiClient.delete(`/admin/reviews/${id}`).then(res => res.data),
  
  // Vlogs
  getVlogs: () => apiClient.get('/admin/vlogs').then(res => res.data),
  createVlog: (data) => apiClient.post('/admin/vlogs', data).then(res => res.data),
  updateVlog: (id, data) => apiClient.put(`/admin/vlogs/${id}`, data).then(res => res.data),
  deleteVlog: (id) => apiClient.delete(`/admin/vlogs/${id}`).then(res => res.data),
  
  // Settings
  getSettings: () => apiClient.get('/admin/settings').then(res => res.data),
  updateSettings: (data) => apiClient.put('/admin/settings', data).then(res => res.data),
  
  // About
  getAbout: () => apiClient.get('/admin/about').then(res => res.data),
  updateAbout: (data) => apiClient.put('/admin/about', data).then(res => res.data),
};

// Export admin API with alias for convenience
export const admin = adminAPI;

export default apiClient;
